/**
 * Cookie utility for handling browser cookies with full unicode support
 * Based on Mozilla's cookie framework
 * https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
 */

export interface CookieOptions {
  expires?: number | string | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
}

/**
 * Gets a cookie value by name
 */
export const getItem = (key: string): string | null => {
  if (!key) {
    return null;
  }
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          `(?:(?:^|.*;)\\s*${encodeURIComponent(key).replace(
            /[-+*.]/g,
            "\\$&"
          )}\\s*=\\s*([^;]*).*$)|^.*$`
        ),
        "$1"
      )
    ) || null
  );
};

/**
 * Sets a cookie with the given key and value
 */
export const setItem = (key: string, value: string, options: CookieOptions = {}): boolean => {
  if (!key || /^(?:expires|max-age|path|domain|secure)$/i.test(key)) {
    return false;
  }

  const { expires, path, domain, secure } = options;
  let expiresString = "";

  if (expires) {
    switch (typeof expires) {
      case "number":
        expiresString =
          expires === Infinity ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT" : `; max-age=${expires}`;
        break;
      case "string":
        expiresString = `; expires=${expires}`;
        break;
      default:
        expiresString = `; expires=${(expires as Date).toUTCString()}`;
        break;
    }
  }

  document.cookie =
    `${encodeURIComponent(key)}=${encodeURIComponent(value)}${expiresString}` +
    (domain ? `; domain=${domain}` : "") +
    (path ? `; path=${path}` : "") +
    (secure ? "; secure" : "");

  return true;
};

/**
 * Removes a cookie by name
 */
export const removeItem = (key: string, path?: string, domain?: string): boolean => {
  if (!hasItem(key)) {
    return false;
  }

  document.cookie =
    `${encodeURIComponent(key)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
    (domain ? `; domain=${domain}` : "") +
    (path ? `; path=${path}` : "");

  return true;
};

/**
 * Checks if a cookie exists
 */
export const hasItem = (key: string): boolean => {
  if (!key) {
    return false;
  }

  return new RegExp(`(?:^|;\\s*)${encodeURIComponent(key).replace(/[-+*.]/g, "\\$&")}\\s*=`).test(
    document.cookie
  );
};

/**
 * Returns an array of all cookie names
 */
export const keys = (): string[] => {
  const cookieKeys = document.cookie
    .replace(/((?:^|\s*;)[^=]+)(?=;|$)|^\s*|\s*(?:[^;]*)?(?:;\s*)/g, "")
    .split(/\s*(?:[^;]*)?;\s*/);

  for (let i = 0; i < cookieKeys.length; i += 1) {
    cookieKeys[i] = decodeURIComponent(cookieKeys[i]);
  }

  return cookieKeys;
};

// For backward compatibility, maintain the Cookies object
export const Cookies = {
  getItem,
  setItem,
  removeItem,
  hasItem,
  keys,
};

export default Cookies;
